<template>
  <div>
    <base-label :id="createFromType" :label="createFromType"/>
    <base-select :value="selectedOptionId"
                 :options="options"
                 text-attribute="name"
                 value-attribute="id"
                 class="w-full border-gray mt-1 h-10"
                 @input="emitSelectOption"
    />

    <transition name="slide-fade" mode="out-in">
      <div v-if="selectedOptionId" class="mt-4">
        <div class="flex items-center">
          <checkbox id="cloneVariablesCheckbox"
                    :is-checked="shouldCloneVariables"
                    @update:isChecked="emitOnChecked"
          />
          <base-label id="cloneVariablesCheckbox"
                      class="ml-2 text-gray-dark cursor-pointer font-medium clone-variables-label"
                      label="Clone variables?"
                      label-classes="font-medium"
                      tooltip="All variables identified by the 'Existing prefix' will be duplicated and their variable name prefix will be replaced with the 'New prefix'."
          >
            Clone variables?
          </base-label>
        </div>
        <div v-if="shouldCloneVariables" class="grid grid-cols-2 gap-2 mt-2">
          <div class="col-span-1">
            <base-label id="existingVariablePrefix" label="Existing prefix" class="ml-2" />
            <base-input id="prefixFrom"
                        :disabled="isPrefixFromInputDisabled"
                        name="prefixFrom"
                        variant="editor"
                        type="string"
                        :value="prefixFrom"
                        class="w-full border-gray py-px mt-1 h-10"
                        :class="{
                          'cursor-not-allowed': isPrefixFromInputDisabled,
                          'border-focus-error focus:border-focus-error': isExistingVariablesPrefixEmptyError || isExistingVariablesPrefixMissingError
                        }"
                        @input="emitUpdatePrefixFrom"
            />
            <span v-if="isExistingVariablesPrefixEmptyError" class="text-sm text-focus-error">Prefix cannot be empty.</span>
            <span v-else-if="isExistingVariablesPrefixMissingError" class="text-sm text-focus-error">{{ errorDescription }}</span>
            <span v-else class="text-sm text-gray">e.g. {{ prefixFrom }}email-address</span>
          </div>

          <div class="col-span-1">
            <base-label id="newVariablePrefix" label="New prefix" class="ml-2" />
            <base-input id="prefixTo"
                        name="prefixTo"
                        variant="editor"
                        type="string"
                        :value="prefixTo"
                        class="w-full border-gray py-px mt-1 h-10"
                        :class="{'border-focus-error focus:border-focus-error': isNewVariablesPrefixError || isSamePrefixError}"
                        @input="emitUpdatePrefixTo"
            />
            <span v-if="isNewVariablesPrefixError" class="text-sm text-focus-error">Prefix cannot be empty.</span>
            <span v-else-if="isSamePrefixError" class="text-sm text-focus-error">{{ errorDescription }}</span>
            <span v-else class="text-sm text-gray">e.g. {{ prefixTo }}email-address</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Checkbox from '@/components/preview/Checkbox'
import previewModule from '@/store/modules/preview.module'
import last from 'ramda/src/last'

export default {
  name: 'SettingsBotCreateFromExisting',
  components: { Checkbox },
  props: {
    createFromType: {
      type: String,
      default: ''
    },
    selectedOptionId: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    shouldCloneVariables: {
      type: Boolean,
      default: false
    },
    isPrefixFromInputDisabled: {
      type: Boolean,
      default: false
    },
    prefixFrom: {
      type: String,
      default: ''
    },
    prefixTo: {
      type: String,
      default: ''
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    isExistingVariablesPrefixEmptyError() {
      return this.error?.path && last(this.error.path) === 'prefix_original'
    },
    isExistingVariablesPrefixMissingError() {
      return this.errorDescription?.startsWith('Variables')
    },
    isNewVariablesPrefixError() {
      return this.error?.path && last(this.error.path) === 'prefix_converted'
    },
    isSamePrefixError() {
      return this.errorDescription === 'Prefixes cannot be the same'
    }
  },
  methods: {
    emitOnChecked(isChecked) {
      this.$emit('on-checked', isChecked)
    },
    emitUpdatePrefixFrom(prefixFrom) {
      if (this.isExistingVariablesPrefixEmptyError || this.isExistingVariablesPrefixMissingError || this.isSamePrefixError) this.error = null
      this.$emit('update-prefix-from', prefixFrom)
    },
    emitUpdatePrefixTo(prefixTo) {
      if (this.isNewVariablesPrefixError || this.isSamePrefixError) this.error = null
      this.$emit('update-prefix-to', prefixTo)
    },
    emitSelectOption(optionId) {
      this.$emit('select-option', optionId)
    }
  },
  destroyed() {
    this.error = null
  }
}
</script>
